import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { queryJobs } from '@/features/jobs/data/jobs';
import {
  buildStageNameFilters,
  buildStagePipelineFilters,
} from '@/features/pipelines/hooks/stagePageFilters';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { BuildStatus, JobListItemDto } from '@flowqa/shared';

type StageHistoryJobStage = {
  id: string;
  pipelineId: string | null;
  stageName: string;
};

type StageHistoryJobParentStage = StageHistoryJobStage & {
  jobCount: number;
};

type UseStageHistoryJobsArgs = {
  isAuthenticated: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  selectedStatuses: BuildStatus[];
  selectedTags: string[];
  expandedStages: readonly StageHistoryJobStage[];
  effectiveStartDay: string | null;
  effectiveEndDay: string | null;
  isLatestMode: boolean;
  hasLatestTimestamp: boolean;
};

type UseStageHistoryJobsResult = {
  jobsByStageId: ReadonlyMap<string, readonly JobListItemDto[]>;
  loadingStageIds: ReadonlySet<string>;
  isFetching: boolean;
  isError: boolean;
};

export const shouldLoadStageHistoryJobs = ({
  isAuthenticated,
  expandedStageCount,
  isLatestMode,
  hasLatestTimestamp,
}: {
  isAuthenticated: boolean;
  expandedStageCount: number;
  isLatestMode: boolean;
  hasLatestTimestamp: boolean;
}): boolean => isAuthenticated && expandedStageCount > 0 && (!isLatestMode || hasLatestTimestamp);

export const resolveStageHistoryJobScope = (
  expandedStages: readonly StageHistoryJobStage[],
): { stageIds: Set<string>; pipelineIds: string[]; stageNames: string[] } => {
  const stageIds = new Set<string>();
  const pipelineIds = new Set<string>();
  const stageNames = new Set<string>();

  expandedStages.forEach((stage) => {
    stageIds.add(stage.id);
    if (stage.pipelineId !== null) {
      pipelineIds.add(stage.pipelineId);
    }
    if (stage.stageName.trim().length > 0) {
      stageNames.add(stage.stageName);
    }
  });

  return {
    stageIds,
    pipelineIds: [...pipelineIds].sort((left, right) => left.localeCompare(right)),
    stageNames: [...stageNames].sort((left, right) => left.localeCompare(right)),
  };
};

export const resolveExpandedStageHistoryJobStages = (
  stages: readonly StageHistoryJobParentStage[],
  expandedRowIds: ReadonlySet<string>,
): StageHistoryJobStage[] =>
  stages
    .filter((stage) => expandedRowIds.has(stage.id) && stage.jobCount > 0)
    .map((stage) => ({
      id: stage.id,
      pipelineId: stage.pipelineId,
      stageName: stage.stageName,
    }));

export const useStageHistoryJobs = ({
  isAuthenticated,
  selectedOrganisationId,
  selectedProjectId,
  selectedStatuses,
  selectedTags,
  expandedStages,
  effectiveStartDay,
  effectiveEndDay,
  isLatestMode,
  hasLatestTimestamp,
}: UseStageHistoryJobsArgs): UseStageHistoryJobsResult => {
  const expandedStageScope = useMemo(
    () => resolveStageHistoryJobScope(expandedStages),
    [expandedStages],
  );
  const queryEnabled = shouldLoadStageHistoryJobs({
    isAuthenticated,
    expandedStageCount: expandedStages.length,
    isLatestMode,
    hasLatestTimestamp,
  });
  const currentStageJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'jobs',
      selectedOrganisationId ?? null,
      selectedProjectId ?? null,
      expandedStageScope.pipelineIds.join(','),
      expandedStageScope.stageNames.join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      effectiveStartDay,
      effectiveEndDay,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(selectedProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: mergeFilters(
              buildInFilter('status', selectedStatuses),
              buildStagePipelineFilters(expandedStageScope.pipelineIds),
              buildStageNameFilters(expandedStageScope.stageNames),
              buildInFilter('tags', selectedTags),
              buildDateRangeFilters(
                'startedAt',
                effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
                effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
              ),
            ),
          }),
      ),
  });

  const jobsByStageId = useMemo(() => {
    const map = new Map<string, JobListItemDto[]>();
    (currentStageJobsQuery.data?.items ?? []).forEach((job) => {
      const stageId = `${job.pipelineId ?? 'null'}:${job.stageName ?? ''}`;
      const items = map.get(stageId) ?? [];
      items.push(job);
      map.set(stageId, items);
    });
    return map;
  }, [currentStageJobsQuery.data?.items]);

  return {
    jobsByStageId,
    loadingStageIds:
      queryEnabled && (currentStageJobsQuery.data === undefined || currentStageJobsQuery.isFetching)
        ? expandedStageScope.stageIds
        : new Set<string>(),
    isFetching: currentStageJobsQuery.isFetching,
    isError: currentStageJobsQuery.isError,
  };
};
